import React, { useState, useEffect, useMemo, useCallback } from "react";
import {useTable, useFlexLayout, useSortBy, usePagination} from 'react-table';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { StickyContainer, Sticky } from 'react-sticky';
import { Label, Spinner, Row, Col } from 'reactstrap';
import Select from 'react-select';
import useDebounce from '@/useDebounce';
import { CONFIGURATION_COLUMNS } from './columns';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import {Sorting} from "@/shared/apiTableHelper"
import getRewardPlatform from "@/service/getRewardPlatform";
import {labelizeNamedData} from '@/shared/helpers';

const TangoSettingsTable = () => {
    const [data, setData] = useState([]);
    const [filterName, setFilterName] = useState('');
    const [filterPlatform, setFilterPlatform] = useState('');
    const [filterMode, setFilterMode] = useState('');
    const debouncedName = useDebounce(filterName, 2000);
    const [platformOptions, setPlatformOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const history = useHistory();

    const fetchData = async (params = {}) => {
        setIsLoading(true);
        try {
            const result = await axios.get(`/tango-settings`, { params });
            setData(Array.isArray(result.data) ? result.data : []);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getRewardPlatform().then(result => {
            const filtered = (result || []).filter(Boolean);
            setPlatformOptions(labelizeNamedData(filtered));
        }).catch(err => console.error('Failed to load platforms:', err));
    }, []);

    useEffect(() => {
        fetchData({
            name: debouncedName || undefined,
            platform: filterPlatform || undefined,
            mode: filterMode || undefined,
        });
    }, [debouncedName, filterPlatform, filterMode]);

    const handleDeleteClick = useCallback((id) => {
        setSelectedId(id);
        setDeleteModalOpen(true);
    }, []);

    const handleEditClick = useCallback((id) => {
        history.push(`/reward-platform-settings/edit/${id}`);
    }, [history]);

    const handleViewClick = useCallback((id) => {
        history.push(`/reward-platform-settings/view/${id}`);
    }, [history]);

    const handleAddClick = () => {
        history.push("/reward-platform-settings/create");
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/tango-settings/delete/${selectedId}`);
            fetchData();
            setDeleteModalOpen(false);
            alert(`Configuration with ID ${selectedId} has been successfully deleted.`);
        } catch (error) {
            alert(`Failed to delete configuration: ${error.message}`);
        }
    };

    const modeOptions = [
        { value: 'prod', label: 'Production' },
        { value: 'test', label: 'Test' },
    ];

    const columns = useMemo(() => CONFIGURATION_COLUMNS(handleDeleteClick, handleEditClick, handleViewClick), [handleDeleteClick, handleEditClick, handleViewClick]);

    const tableInstance = useTable(
        { columns: columns, data },
        useFlexLayout
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow
    } = tableInstance;

    return (
        <div>
            <button onClick={() => history.push("/reward-platform-settings/create")} className="btn btn-primary mb-3">Add Configuration</button>
            <div style={{ position: 'relative', zIndex: 10 }}>
            <Row className="mb-3">
                <Col md={3}>
                    <Label className="mb-1" style={{ fontSize: '12px' }}>Name</Label>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search by name..."
                        value={filterName}
                        onChange={e => setFilterName(e.target.value)}
                    />
                </Col>
                <Col md={2}>
                    <Label className="mb-1" style={{ fontSize: '12px' }}>Platform Type</Label>
                    <Select
                        options={platformOptions}
                        value={platformOptions.find(o => o.label === filterPlatform) || null}
                        onChange={opt => setFilterPlatform(opt ? opt.label : '')}
                        isClearable
                        placeholder="All"
                        className="react-select"
                        classNamePrefix="react-select"
                        styles={{ menuList: base => ({ ...base, top: 0 }) }}
                    />
                </Col>
                <Col md={2}>
                    <Label className="mb-1" style={{ fontSize: '12px' }}>Configuration</Label>
                    <Select
                        options={modeOptions}
                        value={modeOptions.find(o => o.value === filterMode) || null}
                        onChange={opt => setFilterMode(opt ? opt.value : '')}
                        isClearable
                        placeholder="All"
                        className="react-select"
                        classNamePrefix="react-select"
                        styles={{ menuList: base => ({ ...base, top: 0 }) }}
                    />
                </Col>
            </Row>
            </div>
            <ConfirmDeleteModal
                    isOpen={isDeleteModalOpen}
                    onCancel={() => setDeleteModalOpen(false)}
                    onConfirm={() => confirmDelete()}
            />
            {isLoading && (
                <div className="text-center my-3">
                    <Spinner color="primary" size="sm" /> Loading...
                </div>
            )}
            <table {...getTableProps()} className="table">
                <thead>
                {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => (
                            <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                {rows.map(row => {
                    prepareRow(row);
                    return (
                        <tr {...row.getRowProps()}>
                            {row.cells.map(cell => (
                                <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                            ))}
                        </tr>
                    );
                })}
                </tbody>
            </table>
        </div>
    );
};

export default TangoSettingsTable;